// The state of Settings → Data model → "Your data models" lives in the URL, so the browser's Back returns to the very
// same view: the model (or suggestion) chosen in the list, its tab, the actual data with the entity shown and the
// words looked up, the field filter, the distinct values of a source field, the models ticked for a bulk deploy, and
// the publish or deploy panel with its version and target environment.
// Choosing a model or a suggestion and switching its tab are history steps (push, like choosing a class on the
// Data model tab); toggles within the view replace the entry. The distinct values window is a push (lib/urlmodal.ts).
// The parameters sit next to those of the discovered model (class, examples, …); patchModels touches only its own.

import { VALUE_PAGE, VALUE_SORTS, type ValueSort } from './distinct.ts';

export type ModelTab = 'fields' | 'versions';
export type ModelPanel = 'publish' | 'deploy' | 'bulk';

export interface ModelsView {
  model?: string;
  /** A suggested data model chosen in the list (`sug=`); shown instead of the model. */
  sug?: string;
  tab: ModelTab;
  sel: string[];
  panel?: ModelPanel;
  /** The version a per-model deploy panel deploys */
  dv?: number;
  /** The environment picked in a deploy panel */
  denv?: string;
  /** Version numbers whose diff is expanded */
  vx: number[];
  /** "Show actual data" (`actual=1`): the entities the model finds, one at a time. */
  actual: boolean;
  /** Which of the entities found is shown (`entity=`, from 0). */
  entity: number;
  /** The words an entity was looked up by (`mfind=`). */
  find?: string;
  /** The actual data of the working copy although a version is deployed (`ldraft=1`). */
  ldraft: boolean;
  /** The field filter of the field table (`mfilter=`). */
  filter: string;
  /** The suggestions folded away in the list (`sfold=1`). */
  sfold: boolean;
  /** The source field whose distinct values are open (`mvf=<class>|<path>`), how they are sorted, filtered and how many listed. */
  mvf?: string;
  mvs: ValueSort;
  mvq: string;
  mvn: number;
}

const PANELS: readonly string[] = ['publish', 'deploy', 'bulk'];
const KEYS = ['model', 'sug', 'mtab', 'sel', 'panel', 'dv', 'denv', 'vx', 'actual', 'entity', 'mfind', 'ldraft', 'mfilter', 'sfold', 'mvf', 'mvs', 'mvq', 'mvn'] as const;
const list = (s: string | null) => (s ? s.split(',').filter(Boolean) : []);
const posInt = (s: string | null) => { const n = Number(s); return s && Number.isInteger(n) && n > 0 ? n : undefined; };

export function readModels(p: URLSearchParams): ModelsView {
  const panel = p.get('panel');
  const entity = Number(p.get('entity'));
  return {
    model: p.get('model') || undefined,
    sug: p.get('sug') || undefined,
    tab: p.get('mtab') === 'versions' ? 'versions' : 'fields',
    sel: [...new Set(list(p.get('sel')))],
    panel: panel && PANELS.includes(panel) ? (panel as ModelPanel) : undefined,
    dv: posInt(p.get('dv')),
    denv: p.get('denv') || undefined,
    vx: [...new Set(list(p.get('vx')).map(Number).filter((n) => Number.isInteger(n) && n > 0))],
    actual: p.get('actual') === '1',
    entity: Number.isInteger(entity) && entity > 0 ? Math.min(entity, 100_000) : 0,
    find: p.get('mfind')?.trim() || undefined,
    ldraft: p.get('ldraft') === '1',
    filter: p.get('mfilter') ?? '',
    sfold: p.get('sfold') === '1',
    mvf: p.get('mvf')?.includes('|') ? p.get('mvf')! : undefined,
    mvs: VALUE_SORTS.find((k) => k === p.get('mvs')) ?? 'common',
    mvq: p.get('mvq') ?? '',
    mvn: Math.max(VALUE_PAGE, Math.min(5000, Math.round(Number(p.get('mvn')) / VALUE_PAGE) * VALUE_PAGE || VALUE_PAGE)),
  };
}

/** The parameters with the given part of the view changed; every other parameter stays. Defaults are left out. */
export function patchModels(p: URLSearchParams, patch: Partial<ModelsView>): URLSearchParams {
  const v = { ...readModels(p), ...patch };
  const out = new URLSearchParams(p);
  const set = (k: string, value: string | undefined) => (value ? out.set(k, value) : out.delete(k));
  set('model', v.model);
  set('sug', v.sug);
  set('mtab', v.tab === 'versions' ? 'versions' : undefined);
  set('sel', v.sel.length ? v.sel.join(',') : undefined);
  set('panel', v.panel);
  set('dv', v.panel === 'deploy' && v.dv ? String(v.dv) : undefined);
  set('denv', v.panel === 'deploy' || v.panel === 'bulk' ? v.denv : undefined);
  set('vx', v.vx.length ? v.vx.join(',') : undefined);
  set('actual', v.actual ? '1' : undefined);
  // Which entity and what was looked up belong to the actual data: none without it
  set('entity', v.actual && v.entity > 0 ? String(v.entity) : undefined);
  set('mfind', v.actual ? v.find?.trim() || undefined : undefined);
  set('ldraft', v.actual && v.ldraft ? '1' : undefined);
  set('mfilter', v.filter || undefined);
  set('sfold', v.sfold ? '1' : undefined);
  set('mvf', v.mvf);
  set('mvs', v.mvf && v.mvs !== 'common' ? v.mvs : undefined);
  set('mvq', v.mvf ? v.mvq || undefined : undefined);
  set('mvn', v.mvf && v.mvn > VALUE_PAGE ? String(v.mvn) : undefined);
  return out;
}

/** What belongs to the model looked at: dropped when another one is chosen (the selection, a bulk panel and "Show actual data" stay). */
const forOne = (p: URLSearchParams): Partial<ModelsView> => {
  const bulk = readModels(p).panel === 'bulk';
  return { tab: 'fields', panel: bulk ? 'bulk' : undefined, dv: undefined, vx: [], entity: 0, find: undefined, ldraft: false, filter: '', mvf: undefined, ...(bulk ? {} : { denv: undefined }) };
};

/** Choose (or, with undefined, leave) a model: everything that belongs to the previously chosen one is dropped, the selection stays. */
export function openModel(p: URLSearchParams, id: string | undefined): URLSearchParams {
  return patchModels(p, { ...forOne(p), model: id, sug: undefined });
}

/** Choose a suggested data model in the list (the model chosen before comes back with Back). */
export function openSuggestion(p: URLSearchParams, id: string): URLSearchParams {
  return patchModels(p, { ...forOne(p), model: undefined, sug: id });
}

/** The distinct values window of a source field (`<class>|<path>`), for lib/urlmodal.ts: opening starts at the top, closing forgets its filter. */
export const readValues = (p: URLSearchParams) => readModels(p).mvf;
export const withValues = (p: URLSearchParams, v: string | undefined) => patchModels(p, { mvf: v, mvq: '', mvs: 'common', mvn: VALUE_PAGE });

/** A source field as the window's key, and back. */
export const sourceKey = (cls: string, path: string) => `${cls}|${path}`;
export function splitSource(key: string): { cls: string; path: string } {
  const i = key.indexOf('|');
  return { cls: key.slice(0, i), path: key.slice(i + 1) };
}

export const modelParamKeys = KEYS;
